import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { adminApi, type DashboardData } from '../../api/admin';
import { useSession } from '../../auth/session';
import { fmtTime } from '../../lib/format';
import { statusUi, ago } from '../../lib/statusUi';
import type { ReadingStatus } from '../../lib/schedule';
import { groupBy } from '../../lib/pmsGroup';
import { deriveColumn, seriesFor } from '../../lib/readingColumns';
import { resolveThreshold } from '../../lib/thresholds';
import { useAsync, Banner, Item, Empty } from './ui';
import ActivityRing from '../../components/ActivityRing';
import BarChart from '../../components/BarChart';
import MultiLineChart, { type LineSeries } from '../../components/MultiLineChart';

// readings.date is bucketed to the logbook's 08:00–08:00 day (see LOGBOOK_DAY_START_HOUR in
// supabase/functions/api/modules/readings.ts) — shift "today" the same way so these line up
// with what's actually stored, instead of a plain midnight-cut calendar date.
const LOGBOOK_DAY_START_MS = 8 * 3600000;
const today = () => new Date(Date.now() - LOGBOOK_DAY_START_MS).toISOString().slice(0, 10);
const daysAgoISO = (n: number) => new Date(Date.now() - n * 86400000 - LOGBOOK_DAY_START_MS).toISOString().slice(0, 10);
const LINE_COLORS = [
  '#2f6bff', '#16a34a', '#dc2626', '#d97706', '#7c3aed', '#0d9488', '#db2777',
  '#4f46e5', '#ea580c', '#059669', '#be185d', '#0891b2', '#65a30d', '#9333ea',
];

const QUICK_LINKS: Array<{ to: string; label: string; icon: string; tone: string }> = [
  { to: '/admin/trends', label: 'Trends & charts', icon: '📈', tone: 'qc-blue' },
  { to: '/admin/readings', label: 'Readings', icon: '📋', tone: 'qc-indigo' },
  { to: '/admin/daily', label: 'Daily logbook', icon: '📖', tone: 'qc-teal' },
  { to: '/admin/audit', label: 'Audit trail', icon: '🧾', tone: 'qc-gray' },
  { to: '/admin/pms', label: 'PMS reports', icon: '🛠️', tone: 'qc-purple' },
  { to: '/admin/pms/scheduler', label: 'PMS scheduler', icon: '🗓️', tone: 'qc-purple' },
  { to: '/complaints', label: 'Complaints', icon: '📣', tone: 'qc-orange' },
  { to: '/admin/materials-report', label: 'Materials report', icon: '📦', tone: 'qc-orange' },
  { to: '/admin/equipment', label: 'Equipment', icon: '🔌', tone: 'qc-blue' },
  { to: '/admin/tags', label: 'NFC tags', icon: '🏷️', tone: 'qc-pink' },
  { to: '/admin/templates', label: 'Form templates', icon: '📝', tone: 'qc-indigo' },
  { to: '/admin/thresholds', label: 'Thresholds', icon: '🎚️', tone: 'qc-red' },
  { to: '/admin/operators', label: 'Operators', icon: '👥', tone: 'qc-green' },
  { to: '/admin/settings', label: 'Settings', icon: '⚙️', tone: 'qc-gray' },
];

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function Dashboard() {
  const session = useSession();
  const { data, error, loading, reload } = useAsync(() => adminApi.dashboard(), [], 'adminDashboard');
  const week = useAsync(
    () => adminApi.listReadings({ from: daysAgoISO(6), to: today() }, 3000),
    [], 'adminDashboardWeek',
  );
  const meta = useAsync(() => adminApi.trends(), [], 'adminDashboardMeta');
  const nav = useNavigate();

  // ---- Today's readings — all numeric fields of one daily-reading equipment, overlaid ----
  const dailyEquipRows = useMemo(
    () => (meta.data?.equipment ?? []).filter((r) => String(r['Category'] ?? 'daily') !== 'pms'),
    [meta.data],
  );
  const [chartEquipmentId, setChartEquipmentId] = useState('');
  useEffect(() => {
    if (!chartEquipmentId && dailyEquipRows.length) setChartEquipmentId(String(dailyEquipRows[0]['Equipment ID']));
  }, [dailyEquipRows, chartEquipmentId]);

  const todayReadings = useAsync(
    () => (chartEquipmentId
      ? adminApi.listReadings({ equipmentId: chartEquipmentId, from: today(), to: today() }, 500)
      : Promise.resolve({ ok: true as const, rows: [] })),
    [chartEquipmentId],
    chartEquipmentId ? `dashboardTodayReadings:${chartEquipmentId}` : undefined,
  );

  const chartSeries: LineSeries[] = useMemo(() => {
    const eqRow = dailyEquipRows.find((r) => String(r['Equipment ID']) === chartEquipmentId);
    const templateId = eqRow ? String(eqRow['Template ID'] ?? '').trim() : '';
    const tpl = meta.data?.templates.find((t) => t.templateId === templateId);
    const fields = (tpl?.fields ?? []).filter((f) => f.dataType === 'decimal' || f.dataType === 'int');
    const rows = todayReadings.data?.rows ?? [];
    const thresholdRows = meta.data?.thresholds ?? [];
    return fields.map((f, i) => ({
      key: f.fieldKey, label: f.label || f.fieldKey, unit: f.unit,
      color: LINE_COLORS[i % LINE_COLORS.length],
      points: seriesFor(rows, f.col || deriveColumn(f.fieldKey, f.sectionKey, f.label)),
      threshold: resolveThreshold(thresholdRows, chartEquipmentId, templateId, f.fieldKey),
    }));
  }, [dailyEquipRows, chartEquipmentId, meta.data, todayReadings.data]);
  const [eqFilter, setEqFilter] = useState<'all' | 'due' | 'overdue'>('all');
  const [catFilter, setCatFilter] = useState<'daily' | 'pms'>('daily');
  const listRef = useRef<HTMLHeadingElement>(null);

  function focusList(f: 'due' | 'overdue') {
    setEqFilter((cur) => (cur === f ? 'all' : f));
    setTimeout(() => listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);
  }

  const dailyEquip = data?.equipment.filter((e) => (e.category ?? 'daily') === 'daily') ?? [];
  const pmsEquip = data?.equipment.filter((e) => e.category === 'pms') ?? [];
  const dailyCount = dailyEquip.length;
  const pmsCount = pmsEquip.length;
  const onSchedulePct = (list: DashboardData['equipment']) =>
    list.length ? Math.round(((list.length - list.filter((e) => e.status === 'OVERDUE').length) / list.length) * 100) : 100;
  const dailyPct = onSchedulePct(dailyEquip);
  const pmsPct = onSchedulePct(pmsEquip);

  const weekChart = useMemo(() => {
    const days: string[] = [];
    for (let i = 6; i >= 0; i--) days.push(daysAgoISO(i));
    const counts = new Map<string, number>(days.map((d) => [d, 0]));
    (week.data?.rows ?? []).forEach((r) => {
      const d = String(r['Date'] ?? '');
      if (counts.has(d)) counts.set(d, (counts.get(d) ?? 0) + 1);
    });
    return days.map((d) => ({
      label: new Date(d).toLocaleDateString([], { weekday: 'short' }),
      value: counts.get(d) ?? 0,
    }));
  }, [week.data]);

  const shownEquip = useMemo(() => {
    const list = (data?.equipment ?? []).filter((e) => (e.category ?? 'daily') === catFilter);
    if (eqFilter === 'due') return list.filter((e) => e.status === 'DUE' || e.status === 'NO_HISTORY');
    if (eqFilter === 'overdue') return list.filter((e) => e.status === 'OVERDUE');
    return list;
  }, [data, eqFilter, catFilter]);
  const shownGroups = useMemo(
    () => (catFilter === 'pms' ? groupBy(shownEquip, (e) => e.pmsGroup || 'Other') : []),
    [shownEquip, catFilter],
  );

  return (
    <div>
      <div className="dashHero">
        <div>
          <h1 className="dashHero-title">{greeting()}, {session?.operatorName?.split(' ')[0] ?? 'Admin'}</h1>
          <div className="dashHero-sub">
            {new Date().toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            {data ? ` · ${data.totals.equipment} equipment monitored` : ''}
          </div>
        </div>
        <button className="sm" onClick={reload}>↻ Refresh</button>
      </div>

      <Banner error={error} />
      {loading && <div className="spinner" />}
      {data && (
        <>
          <div className="ringGrid">
            <div className="ringCard">
              <ActivityRing pct={dailyPct} tone={dailyPct === 100 ? 'ok' : dailyPct >= 70 ? 'warn' : 'bad'} />
              <div className="ringLabel">
                <div className="ringPct">{dailyPct}%</div>
                <div className="ringName">Daily readings on schedule</div>
                <div className="ringSub">{dailyCount} equipment</div>
              </div>
            </div>
            <div className="ringCard">
              <ActivityRing pct={pmsPct} tone={pmsPct === 100 ? 'ok' : pmsPct >= 70 ? 'warn' : 'bad'} />
              <div className="ringLabel">
                <div className="ringPct">{pmsPct}%</div>
                <div className="ringName">PMS checks on schedule</div>
                <div className="ringSub">{pmsCount} equipment</div>
              </div>
            </div>
          </div>

          <div className="statRow">
            <Stat icon="🔌" n={data.totals.equipment} label="Equipment" onClick={() => nav('/admin/equipment')} />
            <Stat icon="📈" n={data.totals.readingsToday} label="Readings today"
              onClick={() => nav(`/admin/readings?from=${today()}&to=${today()}`)} />
            <Stat icon="⏱️" n={data.totals.due} label="Due now" tone={data.totals.due ? 'warn' : undefined}
              active={eqFilter === 'due'} onClick={() => focusList('due')} />
            <Stat icon="⚠️" n={data.totals.overdue} label="Overdue" tone={data.totals.overdue ? 'bad' : undefined}
              active={eqFilter === 'overdue'} onClick={() => focusList('overdue')} />
            <Stat icon="🚨" n={data.totals.abnormalToday} label="Abnormal today" tone={data.totals.abnormalToday ? 'warn' : undefined}
              onClick={() => nav(`/admin/readings?from=${today()}&to=${today()}&status=abnormal`)} />
          </div>

          <div className="spread" style={{ alignItems: 'center' }}>
            <h2 className="sectionHead" style={{ margin: 0 }}>Today's readings</h2>
            {dailyEquipRows.length > 0 && (
              <select
                value={chartEquipmentId} onChange={(e) => setChartEquipmentId(e.target.value)}
                style={{ width: 'auto', minHeight: 34, padding: '4px 10px', fontSize: '.85rem' }}
              >
                {dailyEquipRows.map((r, i) => (
                  <option key={i} value={String(r['Equipment ID'])}>{String(r['Equipment Name'])}</option>
                ))}
              </select>
            )}
          </div>
          <div className="card">
            {meta.loading || todayReadings.loading ? <div className="spinner" /> : (
              <MultiLineChart
                series={chartSeries}
                subtitle="All numeric fields for this equipment, today — tap a legend chip to hide/show a line. Points outside threshold are highlighted in orange/red."
              />
            )}
          </div>

          <h2 className="sectionHead">Readings — last 7 days</h2>
          <div className="card">
            {week.loading ? <div className="spinner" /> : <BarChart data={weekChart} />}
          </div>

          <h2 className="sectionHead">Quick access</h2>
          <div className="quickGrid">
            {QUICK_LINKS.map((q) => (
              <Link key={q.to} to={q.to} className="quickTile">
                <span className={`quickIcon ${q.tone}`}>{q.icon}</span>
                <span className="quickLabel">{q.label}</span>
              </Link>
            ))}
          </div>

          <div className="spread">
            <h2 ref={listRef}>
              Equipment status
              {eqFilter !== 'all' && <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}> — {eqFilter}</span>}
            </h2>
            {eqFilter !== 'all' && <button className="ghost sm" onClick={() => setEqFilter('all')}>show all</button>}
          </div>
          <div className="choice-group" role="tablist" aria-label="Equipment view" style={{ marginBottom: 12 }}>
            <button type="button" className={`choice-btn ${catFilter === 'daily' ? 'on' : ''}`} role="tab" aria-selected={catFilter === 'daily'} onClick={() => setCatFilter('daily')}>
              Daily readings ({dailyCount})
            </button>
            <button type="button" className={`choice-btn ${catFilter === 'pms' ? 'on' : ''}`} role="tab" aria-selected={catFilter === 'pms'} onClick={() => setCatFilter('pms')}>
              PMS checks ({pmsCount})
            </button>
          </div>

          {shownEquip.length === 0 && <Empty icon="🔌">{eqFilter === 'all' ? 'No active equipment here.' : `Nothing ${eqFilter} right now.`}</Empty>}

          {catFilter === 'daily' && (
            <div className="cardlist">
              {shownEquip.map((e) => <EquipItem key={e.equipmentId} e={e} />)}
            </div>
          )}
          {catFilter === 'pms' && shownGroups.map((g) => (
            <div key={g.label} style={{ marginBottom: 4 }}>
              <h3 className="pmsGroup-head">{g.label} <span className="muted">({g.items.length})</span></h3>
              <div className="cardlist">
                {g.items.map((e) => <EquipItem key={e.equipmentId} e={e} />)}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function EquipItem({ e }: { e: DashboardData['equipment'][number] }) {
  const ui = statusUi(e.status as ReadingStatus);
  return (
    <Item
      stripe={ui.tone === 'muted' ? undefined : ui.tone}
      title={e.name}
      meta={<>
        <span>{e.location}</span>
        <span>Last {ago(e.lastReadingAt)}</span>
        <span>Next {fmtTime(e.nextRecommendedAt)}</span>
      </>}
      actions={<span className={`chip ${chip(ui.tone)}`}>{ui.label}</span>}
    />
  );
}

function Stat({ icon, n, label, tone, onClick, active }: {
  icon: string; n: number; label: string; tone?: 'bad' | 'warn'; onClick?: () => void; active?: boolean;
}) {
  return (
    <button type="button" className={`stat statBtn ${tone ?? ''} ${active ? 'on' : ''}`} onClick={onClick}>
      <div className="statIc">{icon}</div>
      <div className="statN">{n}</div>
      <div className="statL">{label} <span className="statGo">›</span></div>
    </button>
  );
}
function chip(t: string) { return t === 'bad' ? 'bad' : t === 'warn' ? 'warn' : t === 'ok' ? 'ok' : ''; }
